import React, { useState } from 'react';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button } from '~components';
import Icon, { faFileCsv } from '~components/icon';

import { useCreateDocumentBatchMutation } from '~main/actions/documentBatches';

import { BulkCopyCsvJob } from './bulkCopyCsvJobs';

interface ComponentProps {
  job: BulkCopyCsvJob;
  dataCy: string;
  disabled?: boolean;
}

/**
 * "Generate Full Impact CSV" button shared by the preview (step 3) and review (step 4) steps of the
 * bulk-copy wizard (TAV-3258). Hands the job to the existing background document engine so the complete
 * budget list arrives as a downloadable file in the generated-documents menu without either step ever
 * having to wait for it.
 */
const GenerateImpactCsvButton: React.FC<ComponentProps> = ({ job, dataCy, disabled = false }) => {
  const { showToast } = useToast();
  const [createDocumentBatch, { isLoading: isGenerating }] = useCreateDocumentBatchMutation();
  // Latches once a generation has started and never clears (Decision 5): the mutation's own `isLoading`
  // only covers the POST that queues the batch, which resolves in milliseconds, long before the CSV
  // exists. Without this a coordinator could queue the same CSV repeatedly.
  const [started, setStarted] = useState(false);

  const onGenerateCsv = async () => {
    try {
      await createDocumentBatch([job]).unwrap();
      setStarted(true);
      showToast('success', 'Full impact CSV generation started');
    } catch (error) {
      showToast('danger', getErrorMessage(error));
    }
  };

  // The latch is deliberately kept out of `loading`: `~components/Button` swaps `loading` children for a
  // nameless spinner, and since the latch never clears that would leave the control stuck looking like it
  // is still working long after the queueing POST has succeeded. Instead the settled state gets its own
  // honest label, and `loading` is left to track only the brief in-flight window of the POST itself.
  const label = started ? 'Full Impact CSV requested' : 'Generate Full Impact CSV';

  return (
    <Button
      color="info"
      onClick={onGenerateCsv}
      loading={isGenerating}
      disabled={isGenerating || started || disabled}
      // `loading` still strips the children while the POST is in flight, so the explicit aria-label stays
      // needed for that window. Driving it from the same `label` as the children means the accessible name
      // can never drift from the visible text once settled (WCAG 2.5.3 Label in Name).
      aria-label={label}
      dataCy={dataCy}
    >
      <Icon icon={faFileCsv} className="me-2" />
      {label}
    </Button>
  );
};

export default GenerateImpactCsvButton;
